import { getChatToken } from "@/services/app/tenant/chat/token";
import { LS_TOKEN_NAME } from "@/constants";
import { getTokenClaims } from "@/utils/impersonation";
import type { ZulipCredentials, ZulipMessage } from "./types";

let cached: ZulipCredentials | null = null;
let inflight: Promise<ZulipCredentials> | null = null;

const SESSION_KEY = "FR_CHAT_ZULIP_CREDS";

// The cached credentials belong to one sign-in (the JWT's sid). sessionStorage
// survives the reload when the tab switches user (Log In As, or logout and a
// different login), and chatting with someone else's Zulip key must not happen.
const currentSignIn = (): string => {
  try {
    return getTokenClaims(localStorage.getItem(LS_TOKEN_NAME))?.sid ?? "";
  } catch {
    return "";
  }
};

function readCache(): ZulipCredentials | null {
  if (cached) return cached;
  try {
    const raw = sessionStorage.getItem(SESSION_KEY);
    if (raw) {
      const stored = JSON.parse(raw);
      if (stored?.owner && stored.owner === currentSignIn() && stored.creds) {
        cached = stored.creds;
        return cached;
      }
      sessionStorage.removeItem(SESSION_KEY);
    }
  } catch {
    // ignore
  }
  return null;
}

function writeCache(creds: ZulipCredentials) {
  cached = creds;
  try {
    sessionStorage.setItem(SESSION_KEY, JSON.stringify({ owner: currentSignIn(), creds }));
  } catch {
    // ignore
  }
}

cached = readCache();

function clearCache() {
  cached = null;
  try {
    sessionStorage.removeItem(SESSION_KEY);
  } catch {
    // ignore
  }
}

function authHeader(email: string, apiKey: string): string {
  return `Basic ${btoa(`${email}:${apiKey}`)}`;
}

function toFormValue(value: unknown): string {
  if (typeof value === "string") return value;
  if (typeof value === "number" || typeof value === "boolean") return String(value);
  return JSON.stringify(value);
}

function buildQuery(params: Record<string, unknown> | undefined): string {
  if (!params) return "";
  const usp = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => {
    if (v === undefined || v === null) return;
    usp.set(k, toFormValue(v));
  });
  const qs = usp.toString();
  return qs ? `?${qs}` : "";
}

async function parseResponse(res: Response): Promise<any> {
  const text = await res.text();
  let body: any = {};
  try {
    body = text ? JSON.parse(text) : {};
  } catch {
    body = {};
  }
  if (!res.ok) {
    const err: any = new Error(body?.msg || `Zulip request failed (${res.status})`);
    err.status = res.status;
    err.code = body?.code;
    throw err;
  }
  return body;
}

async function rawGet(baseUrl: string, email: string, apiKey: string, path: string, params?: Record<string, unknown>) {
  const res = await fetch(`${baseUrl}/api/v1/${path}${buildQuery(params)}`, {
    method: "GET",
    headers: { Authorization: authHeader(email, apiKey) },
  });
  return parseResponse(res);
}

async function rawForm(baseUrl: string, email: string, apiKey: string, method: "POST" | "PUT" | "PATCH" | "DELETE", path: string, params?: Record<string, unknown>) {
  const body = new URLSearchParams();
  Object.entries(params ?? {}).forEach(([k, v]) => {
    if (v === undefined || v === null) return;
    body.set(k, toFormValue(v));
  });
  const res = await fetch(`${baseUrl}/api/v1/${path}`, {
    method,
    headers: {
      Authorization: authHeader(email, apiKey),
      "Content-Type": "application/x-www-form-urlencoded",
    },
    body,
  });
  return parseResponse(res);
}

async function rawUpload(baseUrl: string, email: string, apiKey: string, file: File, signal?: AbortSignal) {
  const body = new FormData();
  body.append("file", file);
  const res = await fetch(`${baseUrl}/api/v1/user_uploads`, {
    method: "POST",
    headers: { Authorization: authHeader(email, apiKey) },
    body,
    signal,
  });
  return parseResponse(res);
}

async function fetchCredentials(force = false): Promise<ZulipCredentials> {
  if (!force) {
    const existing = readCache();
    if (existing) return existing;
  }
  if (inflight) return inflight;
  inflight = (async () => {
    const result = await getChatToken();
    if (!result?.api_key) throw new Error("Chat is not available right now.");
    const me = await rawGet(result.zulip_base_url, result.email, result.api_key, "users/me", {});
    const creds: ZulipCredentials = {
      apiKey: result.api_key,
      email: result.email,
      baseUrl: result.zulip_base_url,
      userId: me.user_id,
    };
    writeCache(creds);
    return creds;
  })();
  try {
    return await inflight;
  } finally {
    inflight = null;
  }
}

export async function zGet(path: string, params?: Record<string, unknown>): Promise<any> {
  const creds = await fetchCredentials();
  try {
    return await rawGet(creds.baseUrl, creds.email, creds.apiKey, path, params);
  } catch (e: any) {
    if (e?.status === 401) {
      clearCache();
      const fresh = await fetchCredentials(true);
      return rawGet(fresh.baseUrl, fresh.email, fresh.apiKey, path, params);
    }
    throw e;
  }
}

export async function zForm(method: "POST" | "PUT" | "PATCH" | "DELETE", path: string, params?: Record<string, unknown>): Promise<any> {
  const creds = await fetchCredentials();
  try {
    return await rawForm(creds.baseUrl, creds.email, creds.apiKey, method, path, params);
  } catch (e: any) {
    if (e?.status === 401) {
      clearCache();
      const fresh = await fetchCredentials(true);
      return rawForm(fresh.baseUrl, fresh.email, fresh.apiKey, method, path, params);
    }
    throw e;
  }
}

function toRelativeUploadPath(url: string, baseUrl: string): string {
  return url.startsWith(`${baseUrl}/`) ? url.slice(baseUrl.length) : url;
}

export async function uploadFile(file: File, opts?: { signal?: AbortSignal }): Promise<{ url: string; filename: string }> {
  const creds = await fetchCredentials();
  try {
    const body = await rawUpload(creds.baseUrl, creds.email, creds.apiKey, file, opts?.signal);
    return { url: toRelativeUploadPath(body.url, creds.baseUrl), filename: body.filename };
  } catch (e: any) {
    if (e?.status === 401) {
      clearCache();
      const fresh = await fetchCredentials(true);
      const body = await rawUpload(fresh.baseUrl, fresh.email, fresh.apiKey, file, opts?.signal);
      return { url: toRelativeUploadPath(body.url, fresh.baseUrl), filename: body.filename };
    }
    throw e;
  }
}

export function isOwnUploadUrl(src: string): boolean {
  if (src.startsWith("/user_uploads/") || src.startsWith("user_uploads/")) return true;
  const base = cached?.baseUrl;
  return !!base && src.startsWith(`${base}/user_uploads/`);
}

export async function getMyUserId(): Promise<number> {
  const creds = await fetchCredentials();
  return creds.userId;
}

export function resolveRealmUrl(url: string | null | undefined): string | null | undefined {
  if (!url || /^https?:\/\//i.test(url)) return url;
  const base = cached?.baseUrl;
  if (!base) return url;
  return `${base}${url.startsWith("/") ? "" : "/"}${url}`;
}

export const resolveAvatarUrl = resolveRealmUrl;

// Mirrors Zulip's own hash-fragment escaping (web/src/internal_url.ts'
// encodeHashComponent) — browsers zealously URI-decode window.location.hash,
// so Zulip hides its encoding by mapping these characters to a "." escape
// instead of the "%" encodeURIComponent would otherwise use.
const HASH_REPLACEMENTS: Record<string, string> = {
  "%": ".", "!": ".21", "'": ".27", "(": ".28", ")": ".29", "*": ".2A", ".": ".2E",
};

function encodeHashComponent(str: string): string {
  return encodeURIComponent(str).replace(/[%!'()*.]/g, (m) => HASH_REPLACEMENTS[m]);
}

// Mirrors Zulip's encode_stream_id (web/src/internal_url.ts): only append the
// (encoded) stream name to the id when doing so doesn't blow up the URL's
// length — otherwise the bare id alone is enough for Zulip to resolve it.
function encodeStreamSlug(streamId: number, streamName: string): string {
  const slug = `${streamId}-${streamName.replace(/ /g, "-")}`;
  const encoded = encodeHashComponent(slug);
  if (encoded.length - slug.length > 10 || encoded.length > slug.length * 1.5) return String(streamId);
  return encoded;
}

/**
 * A permanent link to a message, in the same `#narrow/...` format Zulip's own
 * webapp generates (hash_util.ts's by_conversation_and_time_url) — used as
 * the "[said](...)" link in a quoted/forwarded message (see ChatContext's
 * buildQuoteBlock). Returns null before credentials have loaded (baseUrl
 * unknown yet) rather than emit a broken link.
 */
export function messagePermalink(msg: ZulipMessage): string | null {
  const base = cached?.baseUrl;
  if (!base) return null;
  if (msg.type === "stream" && msg.stream_id !== undefined) {
    const streamName = typeof msg.display_recipient === "string" ? msg.display_recipient : String(msg.stream_id);
    const slug = encodeStreamSlug(msg.stream_id, streamName);
    return `${base}/#narrow/channel/${slug}/topic/${encodeHashComponent(msg.subject ?? "")}/near/${msg.id}`;
  }
  const recipients = Array.isArray(msg.display_recipient) ? msg.display_recipient : [];
  const userIds = recipients.map((r) => r.id).filter((id) => Number.isFinite(id)).sort((a, b) => a - b);
  if (!userIds.length) return null;
  const suffix = userIds.length >= 3 ? "group" : "dm";
  return `${base}/#narrow/dm/${userIds.join(",")}-${suffix}/near/${msg.id}`;
}
